import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Package, ShoppingBag, Mail, X, Monitor, Tags } from "lucide-react";
import { AdminHomepage } from "@/components/AdminHomepage";
import { AdminCategories } from "@/components/AdminCategories";
import { ProductForm } from "@/components/ProductForm";

const DEFAULT_ITEM_CATEGORIES = ["Motors", "Controllers", "Forks", "Wheels", "Handlebars", "Motor Covers", "Plastics"];
const DEFAULT_FITMENTS = ["Surron Light Bee X", "Surron Ultra Bee", "Talaria Sting R", "E-Ride Pro SS 2.0", "Stark Varg"];

const emptyProduct = { name: "", slug: "", category: "Motors", price: 0, description: "", image_url: "", images: [], stock: 0, compatibility: [], options_label: "", options: [], featured: false, badge: "" };

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export function Admin() {
  const { user, loading: authLoading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [itemCats, setItemCats] = useState(DEFAULT_ITEM_CATEGORIES);
  const [fitments, setFitments] = useState(DEFAULT_FITMENTS);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = () => {
    api.get("/products?category=all").then(({ data }) => setProducts(data));
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/contact").then(({ data }) => setMessages(data)).catch(() => {});
    api.get("/categories").then(({ data }) => {
      const items = data.filter((c) => c.type === "item").map((c) => c.name);
      const bikes = data.filter((c) => c.type === "bike").map((c) => c.name);
      if (items.length) setItemCats(items);
      if (bikes.length) setFitments(bikes);
    }).catch(() => {});
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await login(email, password);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing?.id) {
        await api.put(`/products/${editing.id}`, editing);
        toast.success("Product updated");
      } else {
        await api.post("/products", editing);
        toast.success("Product created");
      }
      setShowForm(false);
      setEditing(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (authLoading) {
    return <div className="flex min-h-screen items-center justify-center pt-[73px]"><p className="font-mono text-slate-500">Loading...</p></div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-[73px]">
        <form onSubmit={handleLogin} className="w-full max-w-md border border-white/10 bg-surface p-8" data-testid="admin-login-form">
          <div className="mb-8 text-center">
            <img src="/logo.png" alt="DetroitEmoto" className="mx-auto h-20 w-auto" data-testid="login-logo" />
            <h1 className="mt-4 font-display text-3xl font-bold uppercase text-white">Admin Login</h1>
          </div>
          {loginError && <p className="mb-4 text-center text-sm text-red-400" data-testid="login-error">{loginError}</p>}
          <div className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-white/10 bg-obsidian px-4 py-3 text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
              data-testid="admin-email"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-white/10 bg-obsidian px-4 py-3 text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
              data-testid="admin-password"
            />
            <button type="submit" className="w-full bg-volt py-3 font-mono text-sm font-bold uppercase tracking-widest text-obsidian hover:bg-white" data-testid="admin-login-submit">
              Sign In
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-[73px]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="font-mono text-sm uppercase tracking-widest text-volt">Dashboard</p>
            <h1 className="mt-2 font-display text-4xl font-bold uppercase text-white" data-testid="admin-heading">Admin Panel</h1>
          </div>
          <button
            onClick={() => { setEditing({ ...emptyProduct }); setShowForm(true); }}
            className="flex items-center gap-2 bg-volt px-4 py-2 font-mono text-sm font-bold uppercase text-obsidian hover:bg-white"
            data-testid="add-product-button"
          >
            <Plus className="h-4 w-4" /> Add Product
          </button>
        </div>

        <div className="mb-8 flex gap-2 border-b border-white/10" data-testid="admin-tabs">
          {[
            { id: "products", label: "Products", icon: Package },
            { id: "orders", label: "Orders", icon: ShoppingBag },
            { id: "messages", label: "Messages", icon: Mail },
            { id: "homepage", label: "Homepage", icon: Monitor },
            { id: "categories", label: "Categories", icon: Tags },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 font-mono text-sm uppercase tracking-widest transition-colors ${activeTab === tab.id ? "border-volt text-volt" : "border-transparent text-slate-400 hover:text-white"}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "products" && (
          <div className="overflow-x-auto border border-white/10" data-testid="products-table">
            <table className="w-full text-left">
              <thead className="border-b border-white/10 bg-surface font-mono text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-white/10 last:border-0 hover:bg-surface/50" data-testid={`product-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image_url} alt="" className="h-10 w-10 object-cover" />
                        <span className="font-medium text-white">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-400">{p.category}</td>
                    <td className="px-4 py-3 font-mono text-sm text-volt">${p.price.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-400">{p.stock}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => { setEditing(p); setShowForm(true); }} className="rounded-sm p-1 text-slate-400 hover:text-volt" data-testid={`edit-${p.slug}`}>
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(p.id)} className="rounded-sm p-1 text-slate-400 hover:text-red-400" data-testid={`delete-${p.slug}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="overflow-x-auto border border-white/10" data-testid="orders-table">
            <table className="w-full text-left">
              <thead className="border-b border-white/10 bg-surface font-mono text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">Order ID</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-white/10 last:border-0 hover:bg-surface/50" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-sm text-slate-400">{o.id.slice(-8)}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{o.items.map((i) => `${i.name}${i.option ? ` (${i.option})` : ""} x${i.quantity}`).join(", ")}</td>
                    <td className="px-4 py-3 font-mono text-sm text-volt">${o.total.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 font-mono text-xs uppercase ${o.payment_status === "paid" ? "bg-volt/20 text-volt" : "bg-slate-700 text-slate-300"}`} data-testid={`order-status-${o.id}`}>
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-400">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "homepage" && <AdminHomepage />}

        {activeTab === "categories" && <AdminCategories />}

        {activeTab === "messages" && (
          <div className="space-y-4" data-testid="messages-list">
            {messages.map((m) => (
              <div key={m.id} className="border border-white/10 bg-surface p-4" data-testid={`message-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{m.name}</p>
                    <p className="font-mono text-xs text-slate-500">{m.email}</p>
                  </div>
                  <span className="font-mono text-xs uppercase text-slate-500">{m.subject}</span>
                </div>
                <p className="mt-3 text-sm text-slate-300">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && <p className="font-mono text-sm text-slate-500" data-testid="no-messages">No messages yet</p>}
          </div>
        )}

        {showForm && (
          <ProductForm
            editing={editing}
            setEditing={setEditing}
            onSave={handleSave}
            onClose={() => { setShowForm(false); setEditing(null); }}
            itemCats={itemCats}
            fitments={fitments}
          />
        )}
      </div>
    </div>
  );
}
